import { useState, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight, FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa';
import { changeState } from 'JS/common';
import useQueryParams from 'utils/useQueryParams'

const Pagination = ({ pageInfo, setPageInfo, syncQuery = true, compact = false }) => {
  const { updateQueryParams } = useQueryParams();
  const [pageGroup, setPageGroup] = useState([]);
  const { totalPage, page } = pageInfo;

  // 스타일 정의
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginTop: '32px',
      marginBottom: '24px',
      gap: '12px'
    },
    pagination: {
      display: 'flex',
      alignItems: 'center',
      gap: '2px',
      listStyle: 'none',
      margin: 0,
      padding: 0,
      flexWrap: 'wrap',
      justifyContent: 'center'
    },
    pageItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '36px',
      height: '36px',
      borderRadius: '6px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: '#cbd5e1',
      backgroundColor: '#ffffff',
      color: '#374151',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      userSelect: 'none'
    },
    pageItemHover: {
      backgroundColor: '#f1f5f9',
      borderColor: '#94a3b8',
      color: '#1e293b'
    },
    pageItemActive: {
      backgroundColor: '#3b82f6',
      borderColor: '#3b82f6',
      color: '#ffffff',
      fontWeight: '600',
      boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
    },
    pageItemDisabled: {
      backgroundColor: '#f8fafc',
      borderColor: '#e2e8f0',
      color: '#9ca3af',
      cursor: 'not-allowed'
    },
    navButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '36px',
      height: '36px',
      borderRadius: '6px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: '#cbd5e1',
      backgroundColor: '#ffffff',
      color: '#6b7280',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      userSelect: 'none'
    },
    navButtonHover: {
      backgroundColor: '#f1f5f9',
      borderColor: '#94a3b8',
      color: '#374151'
    },
    navButtonDisabled: {
      backgroundColor: '#f8fafc',
      borderColor: '#e2e8f0',
      color: '#d1d5db',
      cursor: 'not-allowed'
    },
    jumpButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '36px',
      height: '36px',
      borderRadius: '6px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: '#cbd5e1',
      backgroundColor: '#ffffff',
      color: '#6b7280',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      userSelect: 'none'
    },
    jumpButtonHover: {
      backgroundColor: '#f1f5f9',
      borderColor: '#94a3b8',
      color: '#374151'
    },
    jumpButtonDisabled: {
      backgroundColor: '#f8fafc',
      borderColor: '#e2e8f0',
      color: '#d1d5db',
      cursor: 'not-allowed'
    },
    pageInfo: {
      fontSize: '14px',
      color: '#6b7280',
      fontWeight: '500',
      textAlign: 'center'
    }
  };

  // 부모컴포넌트의 종류에 따라 페이지 변경 함수가 달리 작용 됨
  const changePage = p => {
    if (syncQuery) updateQueryParams({ page: p })
    if (page === p) return;
    else changeState(setPageInfo, 'page', p);
  };

  // 페이지 그룹 다르게 보이게 함
  const changePageGroup = p => {
    const arr = [];
    let first =
      p % 10 === 0
        ? p - 9
        : parseInt(p / 10) === 0
        ? 1
        : parseInt(p / 10) * 10 + 1;
    let last =
      p % 10 === 0
        ? p
        : parseInt(p / 10) === 0
        ? 10
        : parseInt(p / 10) * 10 + 10 > totalPage
        ? totalPage
        : parseInt(p / 10) * 10 + 10;
    if (totalPage < 10) {
      first = 1;
      last = totalPage;
    }
    for (let i = first; i < last + 1; i++) {
      arr.push(i);
    }
    setPageGroup(arr);
  };

  const changePara = direction => {
    if (!direction) return;
    changePage(
      direction === 'prev'
        ? page - 10
        : page + 10 > totalPage
        ? totalPage
        : page + 10
    );
  };

  // 첫 페이지로 이동
  const goToFirst = () => {
    if (page > 1) {
      changePage(1);
    }
  };

  // 마지막 페이지로 이동
  const goToLast = () => {
    if (page < totalPage) {
      changePage(totalPage);
    }
  };

  // 이전 페이지로 이동
  const goToPrev = () => {
    if (page > 1) {
      changePage(page - 1);
    }
  };

  // 다음 페이지로 이동
  const goToNext = () => {
    if (page < totalPage) {
      changePage(page + 1);
    }
  };

  useEffect(() => {
    changePageGroup(page);
  }, [pageInfo]);

  const renderPagination = () => {
    const prevCheck = page >= 11;
    const nextCheck =
      totalPage >= 11 ? (pageGroup.includes(totalPage) ? false : true) : false;

    const pageItems = pageGroup.map(nowPage => {
      const isActive = nowPage === page;
      return (
        <li
          key={nowPage}
          onClick={() => changePage(nowPage)}
          style={{
            ...styles.pageItem,
            ...(isActive ? styles.pageItemActive : {})
          }}
          onMouseEnter={(e) => {
            if (!isActive) {
              Object.assign(e.target.style, styles.pageItemHover);
            }
          }}
          onMouseLeave={(e) => {
            if (!isActive) {
              e.target.style.backgroundColor = '#ffffff';
              e.target.style.borderColor = '#cbd5e1';
              e.target.style.color = '#374151';
            }
          }}
        >
          {nowPage}
        </li>
      );
    });

    return (
      <>
        {/* 첫 페이지로 */}
        <li
          onClick={goToFirst}
          style={{
            ...styles.jumpButton,
            ...(page === 1 ? styles.jumpButtonDisabled : {})
          }}
          onMouseEnter={(e) => {
            if (page !== 1) {
              Object.assign(e.target.style, styles.jumpButtonHover);
            }
          }}
          onMouseLeave={(e) => {
            if (page !== 1) {
              e.target.style.backgroundColor = '#ffffff';
              e.target.style.borderColor = '#cbd5e1';
              e.target.style.color = '#6b7280';
            }
          }}
        >
          <FaAngleDoubleLeft size={14} />
        </li>

        {/* 이전 페이지 */}
        <li
          onClick={goToPrev}
          style={{
            ...styles.navButton,
            ...(page === 1 ? styles.navButtonDisabled : {})
          }}
          onMouseEnter={(e) => {
            if (page !== 1) {
              Object.assign(e.target.style, styles.navButtonHover);
            }
          }}
          onMouseLeave={(e) => {
            if (page !== 1) {
              e.target.style.backgroundColor = '#ffffff';
              e.target.style.borderColor = '#cbd5e1';
              e.target.style.color = '#6b7280';
            }
          }}
        >
          <FaChevronLeft size={12} />
        </li>

        {/* 10페이지 이전 */}
        {prevCheck && (
          <li
            onClick={() => changePara('prev')}
            style={styles.jumpButton}
            onMouseEnter={(e) => {
              Object.assign(e.target.style, styles.jumpButtonHover);
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#ffffff';
              e.target.style.borderColor = '#cbd5e1';
              e.target.style.color = '#6b7280';
            }}
          >
            <FaAngleDoubleLeft size={10} />
            <FaAngleDoubleLeft size={10} style={{ marginLeft: '-2px' }} />
          </li>
        )}

        {/* 페이지 번호들 */}
        {pageItems}

        {/* 10페이지 다음 */}
        {nextCheck && (
          <li
            onClick={() => changePara('next')}
            style={styles.jumpButton}
            onMouseEnter={(e) => {
              Object.assign(e.target.style, styles.jumpButtonHover);
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#ffffff';
              e.target.style.borderColor = '#cbd5e1';
              e.target.style.color = '#6b7280';
            }}
          >
            <FaAngleDoubleRight size={10} />
            <FaAngleDoubleRight size={10} style={{ marginLeft: '-2px' }} />
          </li>
        )}

        {/* 다음 페이지 */}
        <li
          onClick={goToNext}
          style={{
            ...styles.navButton,
            ...(page === totalPage ? styles.navButtonDisabled : {})
          }}
          onMouseEnter={(e) => {
            if (page !== totalPage) {
              Object.assign(e.target.style, styles.navButtonHover);
            }
          }}
          onMouseLeave={(e) => {
            if (page !== totalPage) {
              e.target.style.backgroundColor = '#ffffff';
              e.target.style.borderColor = '#cbd5e1';
              e.target.style.color = '#6b7280';
            }
          }}
        >
          <FaChevronRight size={12} />
        </li>

        {/* 마지막 페이지로 */}
        <li
          onClick={goToLast}
          style={{
            ...styles.jumpButton,
            ...(page === totalPage ? styles.jumpButtonDisabled : {})
          }}
          onMouseEnter={(e) => {
            if (page !== totalPage) {
              Object.assign(e.target.style, styles.jumpButtonHover);
            }
          }}
          onMouseLeave={(e) => {
            if (page !== totalPage) {
              e.target.style.backgroundColor = '#ffffff';
              e.target.style.borderColor = '#cbd5e1';
              e.target.style.color = '#6b7280';
            }
          }}
        >
          <FaAngleDoubleRight size={14} />
        </li>
      </>
    );
  };

  if (pageInfo.totalPage <= 1) {
    return null;
  }

  return (
    <div
      style={{
        ...styles.container,
        ...(compact ? { marginTop: 0, marginBottom: 0, gap: 0 } : {}),
      }}
    >
      <ul style={styles.pagination}>
        {renderPagination()}
      </ul>
      {!compact && (
        <div style={styles.pageInfo}>
          {page} / {totalPage} 페이지
        </div>
      )}

      {/* 모바일 반응형 스타일 */}
      <style>{`
        @media (max-width: 768px) {
          .pagination-container {
            padding: 0 16px;
          }
          
          /* 모바일에서 10페이지 점프 버튼 숨기기 */
          .pagination li:nth-child(3),
          .pagination li:nth-last-child(3) {
            display: none !important;
          }
          
          /* 모바일에서 첫/마지막 페이지 버튼도 숨기기 */
          .pagination li:first-child,
          .pagination li:last-child {
            display: none !important;
          }
        }
        
        @media (max-width: 480px) {
          /* 아주 작은 화면에서는 더 적은 페이지만 표시 */
          .pagination {
            max-width: 100%;
            overflow-x: auto;
            scrollbar-width: none;
            -ms-overflow-style: none;
            gap: 1px !important;
          }
          
          .pagination::-webkit-scrollbar {
            display: none;
          }
          
          .pagination li {
            flex-shrink: 0;
            width: 32px !important;
            height: 32px !important;
            font-size: 12px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Pagination;